import React, { useState, useEffect, useCallback } from "react";
import {
  FileText, Activity, Database, Key, Shield, ShieldCheck,
  AlertTriangle, RefreshCw, Eye, EyeOff, Copy, Check, Mail, Smartphone, Loader2
} from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityDashboard = ({ auth, authAxios }) => {
  const [secTab, setSecTab] = useState("audit");
  const [auditLogs, setAuditLogs] = useState([]);
  const [healthChecks, setHealthChecks] = useState([]);
  const [backups, setBackups] = useState([]);
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, statusRes] = await Promise.all([
        authAxios.get(`${API_URL}/api/admin/audit-logs`),
        authAxios.get(`${API_URL}/api/admin/2fa/status`),
      ]);
      setAuditLogs(logsRes.data || []);
      setTwoFAStatus(statusRes.data);
    } catch (e) { logger.error(e); }

    try {
      const healthRes = await authAxios.get(`${API_URL}/api/admin/health-status`);
      setHealthChecks(healthRes.data?.checks || []);
    } catch (e) { /* endpoint may not exist yet */ }

    try {
      const backupRes = await authAxios.get(`${API_URL}/api/admin/backup-status`);
      setBackups(backupRes.data || []);
    } catch (e) { /* endpoint may not exist yet */ }

    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSetup2FA = async () => {
    try {
      const res = await authAxios.post(`${API_URL}/api/admin/2fa/setup`);
      setSetupData(res.data);
      setMessage("");
    } catch (e) {
      setMessage(e.response?.data?.detail || "Failed to setup 2FA");
    }
  };

  const handleVerify2FA = async () => {
    try {
      await authAxios.post(`${API_URL}/api/admin/2fa/verify`, { code: totpCode });
      setMessage("2FA enabled successfully!");
      setSetupData(null);
      setTotpCode("");
      setTwoFAStatus({ enabled: true });
    } catch (e) {
      setMessage(e.response?.data?.detail || "Invalid code");
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm("Are you sure you want to disable 2FA? This reduces your account security.")) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/2fa/disable`);
      setTwoFAStatus({ enabled: false });
      setSetupData(null);
      setMessage("2FA disabled");
    } catch (e) {
      setMessage("Failed to disable 2FA");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getActionColor = (action) => {
    if (action.includes("failed")) return "text-red-600 bg-red-50";
    if (action.includes("success") || action.includes("enabled")) return "text-green-600 bg-green-50";
    if (action.includes("2fa")) return "text-blue-600 bg-blue-50";
    if (action.includes("deleted") || action.includes("disabled")) return "text-orange-600 bg-orange-50";
    return "text-[#475569] bg-gray-50";
  };

  const secTabs = [
    { id: "audit", label: "Audit Logs", icon: FileText },
    { id: "health", label: "Health Status", icon: Activity },
    { id: "backups", label: "Backups", icon: Database },
    { id: "twofa", label: "2FA Settings", icon: Key },
  ];

  return (
    <div className="space-y-6" data-testid="security-dashboard">
      {/* Sub-tabs */}
      <div className="flex gap-2 flex-wrap">
        {secTabs.map((t) => (
          <button key={t.id} onClick={() => setSecTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${secTab === t.id ? "bg-[#1E1B4B] text-white shadow-md" : "bg-white text-[#475569] hover:bg-gray-100 border border-gray-200"}`}
            data-testid={`sec-tab-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
        <button onClick={fetchData} className="ml-auto flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-[#475569] hover:bg-gray-100 border border-gray-200" data-testid="sec-refresh-btn">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Audit Logs Tab */}
      {secTab === "audit" && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="audit-logs-panel">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="p-2 bg-[#1E1B4B] rounded-lg"><FileText size={18} className="text-white" /></div>
            <div>
              <h3 className="font-bold text-[#1E1B4B]">Audit Trail</h3>
              <p className="text-xs text-[#475569]">All admin actions, logins, and security events</p>
            </div>
            <span className="ml-auto text-xs bg-gray-100 px-3 py-1 rounded-full text-[#475569]">{auditLogs.length} entries</span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {auditLogs.length === 0 && <p className="p-6 text-center text-[#475569] text-sm">No audit logs yet</p>}
            {auditLogs.map((log, i) => (
              <div key={log.timestamp ? `${log.action}-${log.timestamp}` : `log-${i}`} className="flex items-start gap-3 px-5 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                <div className={`mt-0.5 px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getActionColor(log.action)}`}>
                  {log.action.replace(/_/g, " ")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#1E1B4B] truncate">{log.user}</span>
                    {log.ip && <span className="text-xs text-[#94a3b8]">from {log.ip}</span>}
                  </div>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <p className="text-xs text-[#475569] mt-0.5">{JSON.stringify(log.details)}</p>
                  )}
                </div>
                <span className="text-xs text-[#94a3b8] whitespace-nowrap">
                  {new Date(log.timestamp).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Health Status Tab */}
      {secTab === "health" && (
        <div className="space-y-4" data-testid="health-panel">
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { label: "API Server", status: "ok", icon: Activity, desc: "FastAPI running on port 8001" },
              { label: "MongoDB", status: healthChecks.length > 0 ? healthChecks[0]?.mongodb || "ok" : "ok", icon: Database, desc: "Primary database" },
              { label: "Email (SMTP)", status: healthChecks.length > 0 ? healthChecks[0]?.email || "unknown" : "unknown", icon: Mail, desc: "Gmail SMTP" },
            ].map((svc) => (
              <div key={svc.label} className="bg-white rounded-2xl shadow-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${svc.status === "ok" ? "bg-green-50" : svc.status === "down" ? "bg-red-50" : "bg-yellow-50"}`}>
                    <svc.icon size={18} className={svc.status === "ok" ? "text-green-600" : svc.status === "down" ? "text-red-600" : "text-yellow-600"} />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-[#1E1B4B]">{svc.label}</h4>
                    <p className="text-xs text-[#475569]">{svc.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${svc.status === "ok" ? "bg-green-500" : svc.status === "down" ? "bg-red-500 animate-pulse" : "bg-yellow-500"}`} />
                  <span className={`text-sm font-semibold ${svc.status === "ok" ? "text-green-600" : svc.status === "down" ? "text-red-600" : "text-yellow-600"}`}>
                    {svc.status === "ok" ? "Healthy" : svc.status === "down" ? "Down" : "Unknown"}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <h4 className="font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Shield size={16} /> Security Status</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { label: "JWT Authentication", status: true },
                { label: "Bcrypt Password Hashing", status: true },
                { label: "Rate Limiting", status: true },
                { label: "Security Headers (CSP, HSTS)", status: true },
                { label: "CORS Restriction", status: true },
                { label: "Webhook Signature Verification", status: true },
                { label: "Input Sanitization", status: true },
                { label: "2FA (TOTP)", status: twoFAStatus?.enabled || false },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-sm">
                  {item.status ? <ShieldCheck size={16} className="text-green-500" /> : <AlertTriangle size={16} className="text-yellow-500" />}
                  <span className="text-[#1E1B4B]">{item.label}</span>
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${item.status ? "bg-green-50 text-green-600" : "bg-yellow-50 text-yellow-600"}`}>
                    {item.status ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Backups Tab */}
      {secTab === "backups" && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="backups-panel">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg"><Database size={18} className="text-blue-600" /></div>
            <div>
              <h3 className="font-bold text-[#1E1B4B]">Automated Backups</h3>
              <p className="text-xs text-[#475569]">Daily MongoDB backups with 30-day retention</p>
            </div>
          </div>
          <div className="p-5">
            {backups.length === 0 ? (
              <div className="text-center py-8">
                <Database size={40} className="mx-auto text-[#94a3b8] mb-3" />
                <p className="text-[#475569] text-sm mb-1">No backup records yet</p>
                <p className="text-xs text-[#94a3b8]">Backups run automatically every 24 hours. The first backup will appear after the server has been running for a day.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {backups.map((b, i) => (
                  <div key={b.collection || `backup-${i}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Database size={16} className="text-blue-500" />
                      <div>
                        <p className="text-sm font-medium text-[#1E1B4B]">{b.collection}</p>
                        <p className="text-xs text-[#475569]">{b.count} documents</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-[#1E1B4B]">{b.date}</p>
                      <p className="text-xs text-[#475569]">{b.backed_up_at ? new Date(b.backed_up_at).toLocaleTimeString("en-IN") : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 2FA Settings Tab */}
      {secTab === "twofa" && (
        <div className="max-w-lg" data-testid="twofa-panel">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-3">
              <div className="p-2 bg-[#E83E8C]/10 rounded-lg"><Key size={18} className="text-[#E83E8C]" /></div>
              <div>
                <h3 className="font-bold text-[#1E1B4B]">Two-Factor Authentication</h3>
                <p className="text-xs text-[#475569]">Extra layer of security for your admin account</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Current Status */}
              <div className={`flex items-center gap-3 p-4 rounded-xl ${twoFAStatus?.enabled ? "bg-green-50 border border-green-200" : "bg-yellow-50 border border-yellow-200"}`}>
                {twoFAStatus?.enabled ? <ShieldCheck size={24} className="text-green-600" /> : <AlertTriangle size={24} className="text-yellow-600" />}
                <div>
                  <p className={`font-semibold text-sm ${twoFAStatus?.enabled ? "text-green-700" : "text-yellow-700"}`}>
                    {twoFAStatus?.enabled ? "2FA is Enabled" : "2FA is Not Enabled"}
                  </p>
                  <p className="text-xs text-[#475569]">
                    {twoFAStatus?.enabled ? "Your account is protected with an authenticator app" : "Enable 2FA for stronger security"}
                  </p>
                </div>
              </div>

              {message && (
                <div className={`p-3 rounded-xl text-sm ${message.includes("success") || message.includes("enabled") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                  {message}
                </div>
              )}

              {!twoFAStatus?.enabled && !setupData && (
                <button onClick={handleSetup2FA} className="w-full bg-[#1E1B4B] text-white py-3 rounded-xl font-semibold text-sm hover:bg-[#2d2a5e] transition-all flex items-center justify-center gap-2" data-testid="setup-2fa-btn">
                  <Smartphone size={16} /> Set Up 2FA
                </button>
              )}

              {setupData && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <p className="text-sm text-[#475569] mb-3">Scan this QR code with your authenticator app</p>
                    <div className="bg-white inline-block p-3 rounded-xl shadow-sm mb-3">
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(setupData.qr_uri)}&size=200x200`} alt="2FA QR Code" className="w-48 h-48" data-testid="qr-code-img" />
                    </div>
                    <p className="text-xs text-[#94a3b8]">Use Google Authenticator, Authy, or any TOTP app</p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-[#475569] mb-2 font-medium">Manual setup key:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded-lg text-sm font-mono text-[#1E1B4B] border border-gray-200">
                        {showSecret ? setupData.secret : "**********************"}
                      </code>
                      <button onClick={() => setShowSecret(!showSecret)} className="p-2 hover:bg-white rounded-lg transition-colors">
                        {showSecret ? <EyeOff size={16} className="text-[#475569]" /> : <Eye size={16} className="text-[#475569]" />}
                      </button>
                      <button onClick={() => copyToClipboard(setupData.secret)} className="p-2 hover:bg-white rounded-lg transition-colors" data-testid="copy-secret-btn">
                        {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} className="text-[#475569]" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1E1B4B] mb-2">Enter the 6-digit code from your app:</label>
                    <div className="flex gap-2">
                      <input type="text" value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-center text-xl tracking-[0.5em] font-mono focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none"
                        placeholder="000000" maxLength={6} data-testid="verify-2fa-input" />
                      <button onClick={handleVerify2FA} disabled={totpCode.length !== 6}
                        className="bg-green-500 text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-green-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="verify-2fa-btn">Verify</button>
                    </div>
                  </div>
                </div>
              )}

              {twoFAStatus?.enabled && (
                <button onClick={handleDisable2FA} className="w-full border-2 border-red-200 text-red-600 py-3 rounded-xl font-semibold text-sm hover:bg-red-50 transition-all" data-testid="disable-2fa-btn">
                  Disable 2FA
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityDashboard;
